// @flow
import moment from "moment"

import FiltersState from "common/records/FiltersState"
import constants from "common/consts/config"
import { DATE_MODES } from "common/consts/date"
import PassengersCount from "common/records/PassengersCount"
import type MultiPlace from "common/records/MultiPlace"
import type SearchDate from "common/records/SearchDate"

type RequestType = {
  origin: string | MultiPlace,
  destination: string | MultiPlace,
  featureName?: string,
  outboundDate: SearchDate,
  inboundDate: ?SearchDate,
  onePerDay?: boolean,
  oneForCity?: boolean,
  waitForRefresh?: boolean,
  preselectedFlightId?: string,
  preselectedFlightPrice?: number,
  partner?: string,
  filters?: {
    price: {
      min?: number,
      max?: number,
    },
    daysInWeek: {
      daysInWeekList: Array<{}>,
    },
    dayTimes: {
      dayTimesList?: Array<{
        min?: number,
        max?: number,
        type: "departure" | "arrival",
      }>,
    },
    stopNumber: number,
    flightDuration: number,
    stopDuration: {
      min?: number,
      max?: number,
    },
    returnFromDifferentAirport: boolean,
    returnToDifferentAirport: boolean,
    noBags: boolean,
    directFlights: boolean,
    sortBy: string,
    airports: {
      selected: Array<string>,
    },
    airlines: {
      selected: Array<string>,
    },
    sortAggregateBy: string,
  },
  passengers: PassengersCount,
  curr?: string,
  device?: string,
}

export default function({
  origin,
  destination,
  featureName,
  outboundDate,
  inboundDate,
  onePerDay,
  oneForCity,
  waitForRefresh,
  preselectedFlightId,
  preselectedFlightPrice,
  partner,
  filters,
  passengers,
  curr,
  device,
}: RequestType) {
  const searchParams = {}

  if (typeof origin === "string") {
    searchParams.flyFrom = origin
  } else {
    searchParams.flyFrom = origin.getSearchId()
  }

  if (typeof destination === "string") {
    searchParams.to = destination
  } else {
    searchParams.to = destination.getSearchId()
  }
  if (featureName) {
    searchParams.featureName = featureName
  }

  searchParams.dateFrom = moment(outboundDate.from).format(constants.apiDateFormat)
  searchParams.dateTo = moment(outboundDate.to).format(constants.apiDateFormat)
  if (inboundDate) {
    if (
      inboundDate.mode === DATE_MODES.INTERVAL ||
      inboundDate.mode === DATE_MODES.SINGLE ||
      inboundDate.mode === DATE_MODES.ANYTIME ||
      inboundDate.mode === DATE_MODES.MONTH
    ) {
      searchParams.typeFlight = "return"
      searchParams.returnFrom = moment(inboundDate.from).format(constants.apiDateFormat)
      searchParams.returnTo = moment(inboundDate.to).format(constants.apiDateFormat)
    } else if (inboundDate.mode === "timeToStay") {
      searchParams.typeFlight = "return"
      searchParams.daysInDestinationFrom = inboundDate.minStayDays
      searchParams.daysInDestinationTo = inboundDate.maxStayDays
      searchParams.returnFrom = ""
      searchParams.returnTo = ""
    } else {
      searchParams.typeFlight = "oneway"
      searchParams.daysInDestinationFrom = inboundDate.minStayDays
      searchParams.daysInDestinationTo = inboundDate.maxStayDays
      searchParams.returnFrom = ""
      searchParams.returnTo = ""
    }
  } else {
    searchParams.typeFlight = "oneway"
    searchParams.returnFrom = ""
    searchParams.returnTo = ""
  }

  if (onePerDay) {
    searchParams.one_per_date = 1 // eslint-disable-line no-underscore-dangle
  } else {
    searchParams.one_per_date = 0 // eslint-disable-line no-underscore-dangle
  }

  if (oneForCity) {
    searchParams.oneforcity = 1 // oneforcity: request.oneForCity ? "1" : "",
  } else {
    searchParams.oneforcity = 0
  }

  if (waitForRefresh) {
    searchParams.wait_for_refresh = 1 // eslint-disable-line no-underscore-dangle
  } else {
    searchParams.wait_for_refresh = 0 // eslint-disable-line no-underscore-dangle
  }

  if (preselectedFlightId) {
    searchParams.preselectedFlightId = preselectedFlightId
  }

  if (preselectedFlightPrice) {
    searchParams.preselectedFlightPrice = preselectedFlightPrice
  }

  if (partner) {
    searchParams.partner = partner
  }

  if (filters) {
    if (filters.price.min) {
      searchParams.price_from = Math.round(filters.price.min) // eslint-disable-line no-underscore-dangle
    }
    if (filters.price.max) {
      searchParams.price_to = Math.round(filters.price.max) // eslint-disable-line no-underscore-dangle
    }

    if (filters.daysInWeek) {
      const { daysInWeekList } = filters.daysInWeek

      const addDaysInWeekFilter = (data, paramKey) => {
        if (!data) {
          return
        }

        searchParams[paramKey] = Object.keys(data)
          .filter(key => data[key])
          .join("")
      }

      addDaysInWeekFilter(daysInWeekList[0], "flyDays")
      addDaysInWeekFilter(daysInWeekList[1], "returnFlyDays")
    }

    if (filters.dayTimes && filters.dayTimes.dayTimesList) {
      const { dayTimesList } = filters.dayTimes

      const addDateTimesFilter = (data, hasDays, paramKeys) => {
        let hasTimes = false

        if (
          data &&
          data.min !== undefined &&
          data.max !== undefined &&
          (data.min !== 0 || data.max !== 24)
        ) {
          const max = data.max % 24
          const min = data.min % 24

          if (data.type === FiltersState.DEFAULT_DAY_TIMES_TYPE) {
            searchParams[paramKeys.dtimefrom] = `${min}:00`
            searchParams[paramKeys.dtimeto] = `${max}:00`
          } else {
            searchParams[paramKeys.atimefrom] = `${min}:00`
            searchParams[paramKeys.atimeto] = `${max}:00`
          }

          hasTimes = true
        }

        if (hasDays || hasTimes) {
          searchParams[paramKeys.flyDaysType] =
            (data && data.type) || FiltersState.DEFAULT_DAY_TIMES_TYPE
        }
      }

      addDateTimesFilter(dayTimesList[0], Boolean(searchParams.flyDays), {
        dtimefrom: "dtimefrom",
        dtimeto: "dtimeto",
        atimefrom: "atimefrom",
        atimeto: "atimeto",
        flyDaysType: "flyDaysType",
      })

      addDateTimesFilter(dayTimesList[1], Boolean(searchParams.returnFlyDays), {
        dtimefrom: "returndtimefrom",
        dtimeto: "returndtimeto",
        atimefrom: "returnatimefrom",
        atimeto: "returnatimeto",
        flyDaysType: "returnFlyDaysType",
      })
    }

    // TODO 'airlines' filter

    if (filters.stopNumber) {
      if (filters.stopNumber === -1) {
        // nothing will happen (-1 means any number of stops <=> default filter)
      } else {
        searchParams.maxstopovers = filters.stopNumber
      }
    }

    if (filters.flightDuration !== FiltersState.MAXIMAL_FLIGHT_DURATION) {
      searchParams.maxFlyDuration = Math.round(filters.flightDuration) // eslint-disable-line no-underscore-dangle
    }

    if (filters.stopDuration.min && filters.stopDuration.min !== FiltersState.MINIMAL_DURATION) {
      searchParams.stopoverfrom = `${filters.stopDuration.min}:00`
    }

    if (filters.stopDuration.max && filters.stopDuration.max !== FiltersState.MAXIMAL_DURATION) {
      searchParams.stopoverto = `${filters.stopDuration.max}:00`
    }

    if (filters.returnFromDifferentAirport !== FiltersState.DEFAULT_RETURN_FROM_DIFFERENT_AIRPORT) {
      searchParams.returnFromDifferentAirport = filters.returnFromDifferentAirport ? 1 : 0
    }

    if (filters.noBags !== FiltersState.DEFAULT_NO_BAGS) {
      searchParams.no_bags = filters.noBags ? 1 : 0
    }

    if (filters.returnToDifferentAirport !== FiltersState.DEFAULT_RETURN_TO_DIFFERENT_AIRPORT) {
      searchParams.returnToDifferentAirport = filters.returnToDifferentAirport ? 1 : 0
    }

    if (filters.directFlights) {
      searchParams.directFlights = 1
    }

    if (!oneForCity && filters.sortBy) {
      searchParams.sort = filters.sortBy
      searchParams.asc = 1
    }

    if (filters.airports.selected.length) {
      searchParams.selectedStopoverAirports = filters.airports.selected.join(",")
      searchParams.selectedStopoverAirportsExclude = true
    }

    if (filters.airlines.selected.length) {
      searchParams.selectedAirlines = filters.airlines.selected.join(",")
      searchParams.selectedAirlinesExclude = true
    }

    if (oneForCity && filters.sortAggregateBy) {
      searchParams.sort = filters.sortAggregateBy
      searchParams.asc = filters.sortAggregateBy === "popularity" ? 0 : 1
    }
  }

  PassengersCount.keysAvailable.forEach(key => {
    searchParams[key] = passengers.get(key)
  })

  if (curr) {
    // currency code
    searchParams.curr = curr
  }

  if (device) {
    searchParams.device = device
  }

  return searchParams
}
